// Chiptune sound: everything is synthesised live with square/triangle/noise voices,
// so there are no asset files to ship.

type Wave = OscillatorType | 'noise';

interface Sfx {
  w: Wave;
  f: number;
  /** End frequency, sweeps. */
  to?: number;
  d: number;
  v?: number;
  /** Extra layered voices. */
  and?: Sfx[];
  delay?: number;
}

const SFX: Record<string, Sfx> = {
  swing: { w: 'noise', f: 2200, to: 500, d: 0.09, v: 0.22 },
  bite: { w: 'square', f: 160, to: 80, d: 0.08, v: 0.14 },
  bash: { w: 'square', f: 200, to: 60, d: 0.1, v: 0.22, and: [{ w: 'noise', f: 1000, d: 0.06, v: 0.12 }] },
  whirl: { w: 'noise', f: 3000, to: 900, d: 0.25, v: 0.16 },
  slam: { w: 'noise', f: 500, to: 60, d: 0.3, v: 0.4, and: [{ w: 'square', f: 90, to: 30, d: 0.25, v: 0.25 }] },
  charge: { w: 'sawtooth', f: 200, to: 700, d: 0.2, v: 0.14 },
  shout: { w: 'sawtooth', f: 220, to: 330, d: 0.25, v: 0.16, and: [{ w: 'square', f: 110, to: 165, d: 0.25, v: 0.12 }] },
  fire: { w: 'noise', f: 1400, to: 250, d: 0.18, v: 0.2, and: [{ w: 'square', f: 300, to: 110, d: 0.14, v: 0.09 }] },
  ice: { w: 'triangle', f: 1600, to: 500, d: 0.16, v: 0.2, and: [{ w: 'square', f: 2400, to: 1200, d: 0.08, v: 0.05 }] },
  zap: { w: 'square', f: 1500, to: 200, d: 0.14, v: 0.14, and: [{ w: 'noise', f: 5000, d: 0.12, v: 0.12 }] },
  warp: { w: 'square', f: 200, to: 1800, d: 0.2, v: 0.12 },
  shoot: { w: 'square', f: 900, to: 150, d: 0.07, v: 0.13, and: [{ w: 'noise', f: 3000, to: 800, d: 0.05, v: 0.15 }] },
  shootE: { w: 'square', f: 600, to: 200, d: 0.08, v: 0.07 },
  shotgun: { w: 'noise', f: 1800, to: 200, d: 0.2, v: 0.35, and: [{ w: 'square', f: 180, to: 40, d: 0.14, v: 0.2 }] },
  throw: { w: 'triangle', f: 500, to: 900, d: 0.1, v: 0.14 },
  rocket: { w: 'noise', f: 800, to: 2200, d: 0.3, v: 0.16 },
  deploy: { w: 'square', f: 400, to: 800, d: 0.1, v: 0.1, and: [{ w: 'square', f: 800, d: 0.08, v: 0.1, delay: 0.1 }] },
  explode: { w: 'noise', f: 900, to: 50, d: 0.45, v: 0.5, and: [{ w: 'square', f: 80, to: 25, d: 0.35, v: 0.3 }] },
  holy: { w: 'triangle', f: 880, to: 1320, d: 0.2, v: 0.2, and: [{ w: 'triangle', f: 1320, to: 1760, d: 0.18, v: 0.1 }] },
  heal: { w: 'triangle', f: 660, to: 990, d: 0.22, v: 0.2 },
  buff: { w: 'triangle', f: 440, to: 880, d: 0.18, v: 0.18, and: [{ w: 'square', f: 660, d: 0.1, v: 0.06, delay: 0.1 }] },
  bone: { w: 'square', f: 300, to: 180, d: 0.07, v: 0.12, and: [{ w: 'noise', f: 3500, d: 0.05, v: 0.1 }] },
  curse: { w: 'sawtooth', f: 150, to: 90, d: 0.3, v: 0.14 },
  poison: { w: 'noise', f: 700, to: 1800, d: 0.2, v: 0.14 },
  cast: { w: 'triangle', f: 500, to: 800, d: 0.14, v: 0.1 },
  hit: { w: 'noise', f: 1800, to: 500, d: 0.05, v: 0.13 },
  hurt: { w: 'square', f: 260, to: 90, d: 0.12, v: 0.2 },
  die: { w: 'noise', f: 700, to: 80, d: 0.16, v: 0.14 },
  pickup: { w: 'square', f: 880, d: 0.05, v: 0.12, and: [{ w: 'square', f: 1320, d: 0.07, v: 0.12, delay: 0.05 }] },
  gold: { w: 'square', f: 1760, d: 0.04, v: 0.08, and: [{ w: 'square', f: 2640, d: 0.08, v: 0.08, delay: 0.04 }] },
  rare: { w: 'triangle', f: 660, d: 0.1, v: 0.25, and: [{ w: 'triangle', f: 880, d: 0.1, v: 0.25, delay: 0.1 }, { w: 'triangle', f: 1320, d: 0.2, v: 0.25, delay: 0.2 }] },
  legendary: { w: 'square', f: 523, d: 0.12, v: 0.2, and: [{ w: 'square', f: 659, d: 0.12, v: 0.2, delay: 0.1 }, { w: 'square', f: 784, d: 0.12, v: 0.2, delay: 0.2 }, { w: 'square', f: 1047, d: 0.3, v: 0.2, delay: 0.3 }] },
  potion: { w: 'triangle', f: 300, to: 900, d: 0.2, v: 0.2 },
  equip: { w: 'square', f: 300, to: 220, d: 0.05, v: 0.1, and: [{ w: 'noise', f: 2500, d: 0.04, v: 0.08 }] },
  click: { w: 'square', f: 1000, d: 0.03, v: 0.08 },
  deny: { w: 'square', f: 150, d: 0.1, v: 0.12 },
  chest: { w: 'square', f: 300, to: 600, d: 0.12, v: 0.15, and: [{ w: 'square', f: 900, d: 0.1, v: 0.1, delay: 0.1 }] },
  shrine: { w: 'triangle', f: 523, d: 0.12, v: 0.22, and: [{ w: 'triangle', f: 784, d: 0.12, v: 0.22, delay: 0.12 }, { w: 'triangle', f: 1047, d: 0.25, v: 0.22, delay: 0.24 }] },
  levelup: { w: 'square', f: 523, d: 0.09, v: 0.2, and: [{ w: 'square', f: 659, d: 0.09, v: 0.2, delay: 0.09 }, { w: 'square', f: 784, d: 0.09, v: 0.2, delay: 0.18 }, { w: 'square', f: 1047, d: 0.3, v: 0.22, delay: 0.27 }] },
  boss: { w: 'sawtooth', f: 90, to: 45, d: 1.0, v: 0.3, and: [{ w: 'noise', f: 300, to: 60, d: 0.8, v: 0.2 }] },
  victory: { w: 'square', f: 392, d: 0.14, v: 0.22, and: [{ w: 'square', f: 523, d: 0.14, v: 0.22, delay: 0.14 }, { w: 'square', f: 659, d: 0.14, v: 0.22, delay: 0.28 }, { w: 'square', f: 784, d: 0.5, v: 0.24, delay: 0.42 }] },
  youdied: { w: 'sawtooth', f: 220, to: 55, d: 1.2, v: 0.25 },
  zone: { w: 'triangle', f: 220, to: 880, d: 0.4, v: 0.18 },
};

/** Per-realm music: root note, scale degrees, tempo. Generates a slow, moody loop. */
interface Tune {
  root: number;
  scale: number[];
  bpm: number;
  bass: number[];
  lead: number[];
  wave: OscillatorType;
}

const MINOR = [0, 2, 3, 5, 7, 8, 10];
const PHRYG = [0, 1, 3, 5, 7, 8, 10];
const DORIAN = [0, 2, 3, 5, 7, 9, 10];
const HARM = [0, 2, 3, 5, 7, 8, 11];

const _ = -1;
const TUNES: Tune[] = [
  { root: 45, scale: DORIAN, bpm: 88, wave: 'triangle', bass: [0, _, 4, _, 2, _, 4, _, 3, _, 5, _, 4, _, 2, _], lead: [7, 9, 11, _, 9, 7, 4, _, 5, 7, 9, _, 7, _, _, _] },
  { root: 40, scale: PHRYG, bpm: 66, wave: 'square', bass: [0, _, _, 0, _, _, 1, _, 0, _, _, 0, _, _, 4, _], lead: [7, _, 8, _, 7, _, _, _, 11, _, 10, _, 8, _, 7, _] },
  { root: 43, scale: HARM, bpm: 80, wave: 'square', bass: [0, _, 0, 4, _, 0, 5, _, 0, _, 0, 4, _, 3, 1, _], lead: [7, 8, 11, 10, 8, _, 7, _, 11, 10, 8, 7, 6, _, 7, _] },
  { root: 38, scale: MINOR, bpm: 112, wave: 'sawtooth', bass: [0, 0, 7, 0, 0, 7, 0, 5, 0, 0, 7, 0, 3, 3, 4, 4], lead: [14, _, 12, _, 10, _, 9, _, 7, _, 9, _, 10, _, 12, _] },
  { root: 50, scale: DORIAN, bpm: 72, wave: 'triangle', bass: [0, _, 2, _, 4, _, 6, _, 5, _, 3, _, 4, _, 2, _], lead: [14, 11, 9, 7, 9, 11, 14, _, 16, 14, 11, 9, 11, _, _, _] },
  { root: 41, scale: MINOR, bpm: 120, wave: 'square', bass: [0, _, 0, 0, 7, _, 0, 5, 3, _, 3, 3, 4, _, 4, 6], lead: [7, 7, _, 9, 10, _, 9, 7, 5, _, 7, 5, 4, _, 2, _] },
  { root: 47, scale: MINOR, bpm: 62, wave: 'triangle', bass: [0, _, _, _, 5, _, _, _, 3, _, _, _, 4, _, _, _], lead: [7, _, _, 9, 10, _, _, _, 9, _, 7, _, 5, _, _, _] },
  { root: 36, scale: PHRYG, bpm: 100, wave: 'sawtooth', bass: [0, 0, 1, 0, 0, 1, 3, 1, 0, 0, 1, 0, 4, 3, 1, 0], lead: [14, _, 15, _, 14, _, 11, _, 8, 10, 11, _, 8, _, 7, _] },
  { root: 48, scale: DORIAN, bpm: 74, wave: 'triangle', bass: [0, _, 4, _, 5, _, 4, _, 3, _, 2, _, 4, _, 4, _], lead: [9, _, 7, _, 11, _, 9, _, 7, _, 5, _, 7, _, _, _] },
];

const SFX_THROTTLE_MS = 45;

class AudioEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  musicGain: GainNode | null = null;
  noiseBuf: AudioBuffer | null = null;
  muted = false;
  musicOn = true;
  /** Voice chat doesn't play through this engine, so it needs telling. */
  onMuteChange: (() => void) | null = null;
  private last = new Map<string, number>();
  private tune: Tune | null = null;
  private step = 0;
  private nextStepAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Must run from a user gesture. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      this.ctx = new Ctor();
    } catch {
      return;
    }
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.45;
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.22;
    this.musicGain.connect(this.master);
    const len = Math.floor(this.ctx.sampleRate * 1);
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.timer = setInterval(() => this.schedule(), 40);
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.45;
    this.onMuteChange?.();
    return this.muted;
  }

  toggleMusic(): boolean {
    this.musicOn = !this.musicOn;
    if (this.musicGain) this.musicGain.gain.value = this.musicOn ? 0.22 : 0;
    return this.musicOn;
  }

  play(name: string, volume = 1): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || !this.master) return;
    const def = SFX[name];
    if (!def) return;
    const now = performance.now();
    if (now - (this.last.get(name) ?? 0) < SFX_THROTTLE_MS) return;
    this.last.set(name, now);
    this.voice(def, ctx.currentTime, volume);
    for (const a of def.and ?? []) this.voice(a, ctx.currentTime + (a.delay ?? 0), volume);
  }

  private voice(s: Sfx, when: number, volume: number, dest?: AudioNode): void {
    const ctx = this.ctx!;
    const gain = ctx.createGain();
    const peak = (s.v ?? 0.15) * volume;
    gain.gain.setValueAtTime(peak, when);
    gain.gain.exponentialRampToValueAtTime(0.0008, when + s.d);
    gain.connect(dest ?? this.master!);
    if (s.w === 'noise') {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      src.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 0.8;
      filter.frequency.setValueAtTime(s.f, when);
      if (s.to) filter.frequency.exponentialRampToValueAtTime(s.to, when + s.d);
      src.connect(filter);
      filter.connect(gain);
      src.start(when);
      src.stop(when + s.d + 0.02);
    } else {
      const osc = ctx.createOscillator();
      osc.type = s.w;
      osc.frequency.setValueAtTime(s.f, when);
      if (s.to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, s.to), when + s.d);
      osc.connect(gain);
      osc.start(when);
      osc.stop(when + s.d + 0.02);
    }
  }

  setMusic(idx: number | null): void {
    const t = idx === null ? null : TUNES[idx] ?? null;
    if (t === this.tune) return;
    this.tune = t;
    this.step = 0;
    if (this.ctx) this.nextStepAt = this.ctx.currentTime + 0.1;
  }

  private schedule(): void {
    const ctx = this.ctx;
    const t = this.tune;
    if (!ctx || !t || !this.musicGain) return;
    if (this.nextStepAt < ctx.currentTime) this.nextStepAt = ctx.currentTime + 0.05;
    const stepLen = 60 / t.bpm / 2;
    while (this.nextStepAt < ctx.currentTime + 0.15) {
      const s = this.step % t.bass.length;
      const section = Math.floor(this.step / t.bass.length) % 4;
      const b = t.bass[s];
      if (b !== _) {
        this.voice({ w: 'triangle', f: this.noteFreq(t, b, -12), d: stepLen * 1.7, v: 0.8 }, this.nextStepAt, 1, this.musicGain);
      }
      // The melody takes turns with silence so the loop doesn't grate.
      if (section !== 2) {
        const l = t.lead[s];
        if (l !== _) {
          this.voice({ w: t.wave, f: this.noteFreq(t, l, section === 3 ? 12 : 0), d: stepLen * 1.5, v: 0.28 }, this.nextStepAt, 1, this.musicGain);
        }
      }
      if (t.bpm >= 100 && s % 4 === 2) {
        this.voice({ w: 'noise', f: 4000, d: 0.04, v: 0.35 }, this.nextStepAt, 1, this.musicGain);
      }
      this.step++;
      this.nextStepAt += stepLen;
    }
  }

  private noteFreq(t: Tune, degree: number, shift: number): number {
    const len = t.scale.length;
    const oct = Math.floor(degree / len);
    const semi = t.scale[((degree % len) + len) % len] + oct * 12;
    return 440 * 2 ** ((t.root + semi + shift - 69) / 12);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
  }
}

export const audio = new AudioEngine();
